import {
  Button,
  DatePicker,
  Form,
  Select,
  Space,
  Upload,
  UploadFile,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import { RcFile, UploadProps } from "antd/es/upload";
import Dragger from "antd/es/upload/Dragger";
import axios from "axios";
import {
  clickHereDraggerStyles,
  headerDraggerStyles,
  subtitleDraggerStyles,
} from "./styles";
import { CustomIcons } from "../../assets";
import Colors from "../../config/Colors";
import { apiUrl } from "../../config/urls";
import Cookies from "js-cookie";

type newCompanyMembersProp = {
  onFormFinish: (company: ICompanyMembers) => Promise<void>;
  onModalClose: () => void;
  isLoading: boolean;
  isSuccess: boolean;
};

const NewCompanyUsers = ({
  onFormFinish,
  onModalClose,
  isLoading,
  isSuccess,
}: newCompanyMembersProp) => {
  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  const [selectedSubPlan, setSelectedSubPlan] = useState();
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [fileUrl, setFileUrl] = useState<string | null>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);

  const onFinish = async () => {
    if (!fileUrl) {
      message.error("Please upload a valid spreadsheet before saving");
      return;
    }

    // Build the payload explicitly so raw antd form values (the upload event
    // object, the dateRange dayjs pair) never leak into the request body.
    const values: ICompanyMembers = {
      id: 0, // overwritten with the company id by the parent handler
      subscription: selectedSubPlan,
      validFrom: startDate,
      validTo: endDate,
      url: fileUrl,
    };

    await onFormFinish(values);
  };

  const handleClose = () => {
    form.resetFields();
    onModalClose();
  };

  const handleSubChange = (value: any) => {
    setSelectedSubPlan(value);
  };
  const handleDateChange = (dates: any, dateString: [string, string]) => {
    setStartDate(dateString[0]);
    setEndDate(dateString[1]);
  };

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess]);

  // Upload endpoint
  const getUploadUrl = () => {
    if (apiUrl) {
      return `${apiUrl}/Admin_Companies/GetSpreadSheetUrl`;
    }
  };

  // Auth headers
  const getAuthHeaders = () => {
    const token = Cookies.get("token");
    const headers: Record<string, string> = {
      Accept: "application/json",
      // "X-API-Client": "SaferCity",
      // "X-API-Version": "1.0.0",
      // "X-Platform": "Web",
      // "X-Tenant": "SaferCity",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  // Custom request using Axios
  const customRequest: UploadProps["customRequest"] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;

    const formData = new FormData();
    formData.append("file", file as RcFile);

    try {
      setUploading(true);
      const response = await axios.post(getUploadUrl()!, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
        onUploadProgress: (event) => {
          if (onProgress) {
            onProgress({
              percent: Math.round((event.loaded / event.total!) * 100),
            });
          }
        },
      });

      setFileUrl(response.data); // adjust if API returns `{ url: "..." }`
      setUploading(false);
      message.success("Excel file uploaded successfully!");
      if (onSuccess) onSuccess(response.data, file as RcFile);
    } catch (err: any) {
      setUploading(false);
      setFileUrl(null);
      // GetSpreadSheetUrl returns its reason as a plain string body (e.g. which
      // header column is wrong) — show that instead of the generic axios message.
      const data = err.response?.data;
      const serverMessage =
        typeof data === "string" && data
          ? data
          : data?.title || data?.message || err.message;
      message.error(`Upload failed: ${serverMessage || "Unknown error"}`, 6);
      if (onError) onError(err);
    }
  };

  const handleUploadChange: UploadProps["onChange"] = (info) => {
    const latestFileList = info.fileList.slice(-1);
    setFileList(latestFileList);

    if (info.file.status === "error" || latestFileList.length === 0) {
      setFileList([]);
      setFileUrl(null);
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isExcelFile =
      file.type === "application/vnd.ms-excel" ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.oasis.opendocument.spreadsheet";

    if (!isExcelFile) {
      message.error(
        `Invalid file type: ${file.type}. Please upload Excel files only.`
      );
      return Upload.LIST_IGNORE;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error("File must be smaller than 10MB!");
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  return (
    <div>
      <Form
        form={form}
        name="addCompanyMembers"
        layout="vertical"
        onFinish={onFinish}
      >
        <div className="col-12">
          <Form.Item
            name={"subscription"}
            label="Select Subscription"
            rules={[
              { required: true, message: "Please select a subscription" },
            ]}
            className="mb-1"
          >
            <Select
              onChange={handleSubChange}
              placeholder="Subscription Plan"
              size="large"
              style={{ width: 485 }}
              options={[
                { value: 2, label: "Basic Plan" },
                { value: 8, label: "Family of Two" },
                { value: 3, label: "Family of Four" },
                { value: 4, label: "Family of Six" },
                { value: 5, label: "Premium" },
                { value: 9, label: "Family of Two Premium" },
                { value: 6, label: "Family of Four Premium" },
                { value: 7, label: "Family of Six Premium" },
              ]}
            />
          </Form.Item>
        </div>

        <div className="col-12">
          <Space
            direction="vertical"
            size={12}
            className="flex flex-row mt-2 align-items-centers"
          >
            <Form.Item
              name={"dateRange"}
              label="Select Contract Period"
              rules={[
                { required: true, message: "Please select contract period" },
              ]}
              className="mb-1"
            >
              <RangePicker onChange={handleDateChange} />
            </Form.Item>
          </Space>
        </div>

        <Form.Item
          name={"fileUrl"}
          label="Upload Excel File"
          // Store the uploaded URL string as the field value instead of the raw
          // upload event, so the required rule fails until the upload succeeds.
          getValueFromEvent={(e: any) =>
            typeof e === "string"
              ? e
              : e?.file?.status === "done"
              ? e.file.response
              : undefined
          }
          rules={[
            {
              required: true,
              message: "Please upload a company excel Spreadsheet",
            },
          ]}
          className="mb-5 col-12"
        >
          <Dragger
            customRequest={customRequest}
            beforeUpload={beforeUpload}
            onChange={handleUploadChange}
            fileList={fileList}
            maxCount={1}
            accept=".xlsx"
            className="w-full"
            style={{ background: "transparent" }}
          >
            <div className="flex flex-col items-center content-center w-full h-full gap-3">
              <CustomIcons.UploadIcon
                width={46}
                height={46}
                svgColor={Colors.appMainColor}
              />
              <div className="flex gap-1 justify-content-center">
                <div className="text-center" style={headerDraggerStyles}>
                  Drop your excel file here or
                </div>
                <div style={clickHereDraggerStyles}>click here</div>
                <div className="text-center" style={headerDraggerStyles}>
                  to upload
                </div>
              </div>

              <div style={subtitleDraggerStyles}>
                File should be excel, .xlsx{" "}
              </div>

              <div className="mt-2 text-xs text-gray-500">
                {uploading && <p>Upload Status: Uploading...</p>}
              </div>
            </div>
          </Dragger>
        </Form.Item>

        <Form.Item className="w-12 pt-2 mb-1">
          <div className="flex w-full gap-2 col-12 justify-content-end align-content-center">
            <Button
              onClick={handleClose}
              type="primary"
              ghost
              size="large"
              className="w-3"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="w-3 bg-[#1757a2] shadow-none"
              loading={isLoading}
              disabled={uploading || !fileUrl}
            >
              Save
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default NewCompanyUsers;
